/**
 * WorkflowActionBar — sticky bottom primary-action bar.
 * Consolidates scattered header buttons into one clear CTA.
 * Adds pb-16 breathing room to the scroll container so content
 * doesn't hide behind the bar.
 *
 * Props:
 *   primary    { label, onClick, loading?, disabled?, style?, className? }
 *   secondary  { label, onClick, className? }   optional
 *   hint       string                            optional contextual hint
 *   visible    boolean                           false → renders nothing
 */
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function WorkflowActionBar({ primary, secondary, hint, visible = true }) {
  if (!visible || !primary) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
      data-testid="workflow-action-bar"
    >
      <div
        className="pointer-events-auto border-t"
        style={{
          background:    "rgba(255,255,255,0.96)",
          backdropFilter:"blur(8px)",
          WebkitBackdropFilter:"blur(8px)",
          borderColor:   "rgba(0,0,0,0.07)",
          boxShadow:     "0 -2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {/* Hint text — left side, hidden on very small screens */}
          {hint && (
            <p className="text-xs text-slate-500 flex-1 min-w-0 hidden sm:block truncate">
              {hint}
            </p>
          )}

          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            {secondary && (
              <Button
                data-testid={secondary.testId || "workflow-secondary-action"}
                size="sm"
                variant="outline"
                onClick={secondary.onClick}
                className={secondary.className || ""}
                disabled={secondary.disabled}
              >
                {secondary.label}
              </Button>
            )}
            <Button
              data-testid={primary.testId || "workflow-primary-action"}
              size="sm"
              disabled={primary.disabled || primary.loading}
              onClick={primary.onClick}
              style={primary.style}
              className={primary.className || ""}
            >
              {primary.loading && (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              )}
              {primary.label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
